// GameStateStore.js - gameState 统一读写入口
import { syncPlayerToGameState } from './utils/PlayerDataSync.js';

class GameStateStoreClass {
    /**
     * @returns {Object|null}
     */
    getState() {
        return window.gameState || null;
    }

    /**
     * @returns {Object|null}
     */
    getPlayer() {
        return window.gameState?.player ?? null;
    }

    /**
     * @param {string} key
     * @returns {*}
     */
    get(key) {
        return window.gameState?.[key];
    }

    /**
     * @param {string} key
     * @param {*} value
     */
    set(key, value) {
        if (window.gameState) {
            window.gameState[key] = value;
        }
    }

    /**
     * @param {string} sceneKey
     */
    setCurrentScene(sceneKey) {
        this.set('currentScene', sceneKey);
    }

    /**
     * @param {Object} partial
     */
    patchPlayer(partial) {
        const player = this.getPlayer();
        if (!player || !partial) {
            return;
        }
        Object.assign(player, partial);
    }

    /**
     * @param {Object} scenePlayer
     * @param {Object} [options]
     */
    syncFromScene(scenePlayer, options = {}) {
        const player = this.getPlayer();
        if (!scenePlayer || !player) {
            return;
        }
        syncPlayerToGameState(scenePlayer, player, options);
    }

    /**
     * @param {Object} playerData
     */
    loadPlayerData(playerData) {
        const player = this.getPlayer();
        if (!player || !playerData) {
            return;
        }
        Object.assign(player, playerData);
    }

    /**
     * 构建完整存档载荷（含场景、队伍、事件）
     * @returns {Object|null}
     */
    buildSavePayload() {
        const state = this.getState();
        const player = this.getPlayer();
        if (!state || !player) {
            return null;
        }

        const currentScene = state.currentScene || player.currentScene || player.initialScene || null;

        return {
            version: '1.0.1',
            player: { ...player, currentScene },
            party: state.party ? JSON.parse(JSON.stringify(state.party)) : [],
            triggeredEvents: state.triggeredEvents ? JSON.parse(JSON.stringify(state.triggeredEvents)) : [],
            currentScene
        };
    }

    /**
     * 应用存档载荷（兼容旧版扁平 player 结构）
     * @param {Object} saveData
     */
    applySavePayload(saveData) {
        if (!saveData || typeof saveData !== 'object') {
            return;
        }

        const isWrapped = saveData.player && typeof saveData.player === 'object';

        if (isWrapped) {
            this.loadPlayerData(saveData.player);
            if (Array.isArray(saveData.party)) {
                this.set('party', saveData.party);
            }
            if (Array.isArray(saveData.triggeredEvents)) {
                this.set('triggeredEvents', saveData.triggeredEvents);
            }
            const scene = saveData.currentScene || saveData.player.currentScene;
            if (scene) {
                this.setCurrentScene(scene);
            }
            return;
        }

        this.loadPlayerData(saveData);
        if (saveData.currentScene) {
            this.setCurrentScene(saveData.currentScene);
        }
    }

    /** @param {number} timestamp */
    setLastEncounterTime(timestamp) {
        this.set('lastEncounterTime', timestamp);
    }

    /**
     * 整体替换 gameState（读档等场景）
     * @param {Object} state
     */
    replaceState(state) {
        window.gameState = state;
    }

    /** @returns {Object|null} */
    getMultiplayer() {
        return window.gameState?.multiplayer ?? null;
    }

    /** @returns {boolean} */
    isMultiplayerConnected() {
        return !!(window.gameState?.multiplayer?.connected);
    }

    /**
     * @param {Object} multiplayerConfig
     */
    setMultiplayer(multiplayerConfig) {
        this.set('multiplayer', multiplayerConfig);
    }

    /** @returns {Object|null} */
    getSettings() {
        return window.gameState?.settings ?? null;
    }

    /**
     * @param {Object} newSettings
     */
    updateSettings(newSettings) {
        const current = this.getSettings() || {};
        this.set('settings', { ...current, ...newSettings });
    }

    /** @returns {Array|null} */
    getTriggeredEvents() {
        if (!window.gameState) {
            return null;
        }
        if (!window.gameState.triggeredEvents) {
            window.gameState.triggeredEvents = [];
        }
        return window.gameState.triggeredEvents;
    }

    /**
     * @param {Object} eventInfo
     */
    pushTriggeredEvent(eventInfo) {
        const events = this.getTriggeredEvents();
        if (events) {
            events.push(eventInfo);
        }
    }
}

const GameStateStore = new GameStateStoreClass();
export default GameStateStore;
